/**
 * Cart page wrapper — re-renders itself from the section HTML that cart.js
 * bundles into every cart mutation ('cart:change' event). Also owns the Change
 * panel on lines with variants, which mirrors the drawer's Edit panel.
 * @extends HTMLElement
 */
import { swapLineVariant } from './component-cart-variant-swap.js';

export class CartPage extends HTMLElement {
  constructor() {
    super();
    this.onCartChange = this.onCartChange.bind(this);
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    document.addEventListener('cart:change', this.onCartChange);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartChange);
    this.removeEventListener('click', this.onClick);
  }

  /** Delegated, because every cart:change replaces this element's children. */
  onClick(event) {
    const toggle = event.target.closest('[data-variant-toggle]');
    if (toggle) {
      event.preventDefault();
      this.toggleVariantPanel(toggle);
      return;
    }

    const option = event.target.closest('.cart-variant-panel__option');
    if (!option) return;

    event.preventDefault();
    if (option.disabled || option.getAttribute('aria-pressed') === 'true') return;

    const variant = this.resolveVariant(option);
    if (variant) this.swapVariant(option, variant);
  }

  toggleVariantPanel(toggle) {
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;

    const willOpen = panel.hidden;
    panel.hidden = !willOpen;
    toggle.setAttribute('aria-expanded', String(willOpen));
  }

  /**
   * Find the variant for the picked option value. The other options keep their
   * current selection; when that exact combination doesn't exist or is sold
   * out, fall back to the closest available variant carrying the picked value.
   * @returns {{ id: number, options: string[], available: boolean } | undefined}
   */
  resolveVariant(option) {
    const panel = option.closest('.cart-variant-panel');
    const variants = JSON.parse(panel.querySelector('[data-variants]')?.textContent || '[]');
    const index = Number(option.dataset.optionIndex);
    const wanted = [...panel.querySelectorAll('.cart-variant-panel__option[aria-pressed="true"]')].map(
      (el) => el.dataset.optionValue,
    );
    wanted[index] = option.dataset.optionValue;

    const exact = variants.find((variant) => variant.available && variant.options.every((value, i) => value === wanted[i]));
    if (exact) return exact;

    const carrying = variants.filter((variant) => variant.available && variant.options[index] === wanted[index]);
    const score = (variant) => variant.options.filter((value, i) => value === wanted[i]).length;
    return carrying.sort((a, b) => score(b) - score(a))[0];
  }

  /**
   * Swap the line to the resolved variant. The add-then-remove sequence lives in
   * component-cart-variant-swap.js, shared with the drawer.
   */
  async swapVariant(option, variant) {
    const lineRoot = option.closest('.cart-line');
    const currentLineItem = window.Cart?.state?.items?.find((item) => item.key === lineRoot?.dataset.lineKey);
    if (!currentLineItem) return;

    const errorEl = lineRoot.querySelector('.cart-line__error');
    if (errorEl) errorEl.textContent = '';
    lineRoot.setAttribute('aria-busy', 'true');

    try {
      const removed = await swapLineVariant({
        oldKey: currentLineItem.key,
        variantId: variant.id,
        quantity: currentLineItem.quantity,
      });
      // The add succeeded but the old line survived every retry — the shopper
      // is left holding both variants, and the refresh below won't explain why.
      if (removed === false && errorEl) errorEl.textContent = option.closest('.cart-variant-panel').dataset.swapError;
    } catch (error) {
      if (errorEl) errorEl.textContent = error.description || error.message;
    } finally {
      await window.Cart?.refresh();
      lineRoot.setAttribute('aria-busy', 'false');
    }
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '');
  }

  onCartChange(event) {
    const html = event.detail.sections?.[this.sectionId];
    if (!html) return;

    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-page');
    if (next) this.innerHTML = next.innerHTML;
  }
}

if (!customElements.get('cart-page')) {
  customElements.define('cart-page', CartPage);
}
